import { MODULES, MODULE_CODES, PERMISSIONS } from '@ibe/constants';
import { Button } from '@ibe/ui';
import { Plus } from 'lucide-react';
import { Link } from 'react-router-dom';

import { PermissionGuard } from '../../../auth/permission-guard.js';
import { ResourceListPage } from '../../../components/resource-list/resource-list-page.js';
import { type FilterDescriptor } from '../../../lib/resource-list/filters.js';
import { tenantEndpoints } from '../api/endpoints.js';
import { tenantRow } from '../api/schemas.js';
import { tenantColumns } from './tenant-columns.js';

const filters: readonly FilterDescriptor[] = [
  {
    id: 'status',
    label: 'Status',
    param: 'status',
    kind: 'select',
    options: [
      { value: 'PENDING', label: 'Pending' },
      { value: 'ACTIVE', label: 'Active' },
      { value: 'SUSPENDED', label: 'Suspended' },
      { value: 'ARCHIVED', label: 'Archived' },
    ],
  },
  {
    id: 'module',
    label: 'Module',
    param: 'module',
    kind: 'select',
    options: MODULE_CODES.filter((code) => code !== MODULES.CORE).map((code) => ({
      value: code,
      // CORE is not offered: every tenant has it, so it filters nothing.
      label: code,
    })),
  },
];

export function TenantListPage(): React.JSX.Element {
  return (
    <ResourceListPage
      title="Tenants"
      description="Every tenant this platform manages."
      path={tenantEndpoints.list}
      resource="tenants"
      schema={tenantRow}
      columns={tenantColumns}
      filters={filters}
      rowKey={(row) => row.id}
      permission={PERMISSIONS.TENANT_READ}
      // Newest first. The list is read straight after creating a tenant, and
      // an alphabetical default buried the new row wherever its name fell.
      // `createdAt` is not a column here, so nothing shows a sort arrow until
      // the reader picks one — which is the honest rendering of "newest first".
      defaultSort={{ columnId: 'createdAt', direction: 'desc' }}
      actions={
        <PermissionGuard permission={PERMISSIONS.TENANT_CREATE}>
          {/* A link, not a button with a handler: creating is a page now,
                so it should be openable in a new tab and reachable by URL. */}
          <Button asChild>
            <Link to="/tenants/new">
              <Plus className="h-4 w-4" aria-hidden="true" />
              New tenant
            </Link>
          </Button>
        </PermissionGuard>
      }
    />
  );
}
